import { Card, CardContent, Stack, Typography } from '@mui/material'
import { CarrierVisitDirection, HoldReaction, TruckVisitWithOrdersDto } from '@planning/app/api'
import { useTranslate } from '@tolgee/react'
import { ColoredLabel, HighlightedText, TrailerIcon, TruckIcon, useMinimalsTheme } from '@tom-ui/ui'
import { observer } from 'mobx-react-lite'
import { FC, useMemo } from 'react'
import { usePlanningStore } from '@planning/AppProvider'
import { getHoldEnforcementResult, masterHoldTypes } from '@planning/utils/hold-enforcement'
import { HoldEnforcementAlert } from '../../../components/hold/HoldEnforcementAlert'
import { TruckVisitDriver } from './Atoms/TruckVisitDriver'
import { TruckVisitExpectedArrival } from './Atoms/TruckVisitExpectedArrival'
import { TruckVisitSearchCargoDetails } from './Molecules/TruckVisitSearchCargoDetails'

export enum TruckAppointmentSearchBy {
  TruckPlate = 'TruckPlate',
  DriverID = 'DriverID',
}

interface Props {
  truckVisitWithOrdersDto: TruckVisitWithOrdersDto
  onClick: (truckVisit: TruckVisitWithOrdersDto) => void
  searchBy: TruckAppointmentSearchBy
  highlight: string
  direction: CarrierVisitDirection
}

export const TruckAppointmentSearchResult: FC<Props> = observer(({
  truckVisitWithOrdersDto,
  onClick,
  searchBy,
  highlight,
  direction,
}) => {
  const { t } = useTranslate()
  const theme = useMinimalsTheme()
  const { holdItemStore } = usePlanningStore()

  const masterTypes = masterHoldTypes.get()

  const enforcement = useMemo(() => {
    const orders = [
      ...(truckVisitWithOrdersDto.inboundOrders ?? []),
      ...(truckVisitWithOrdersDto.outboundOrders ?? []),
    ]

    let result = { reaction: null as HoldReaction | null, holdNames: [] as string[], isBlocking: false }

    orders.forEach(o => {
      const journey = truckVisitWithOrdersDto.containerJourneys?.find(
        j => j.inboundOrderId === o.id || j.outboundOrderId === o.id,
      )
      const holds = journey?.id ? (holdItemStore.holdsByContainerJourneyId[journey.id] ?? []) : []
      const orderResult = getHoldEnforcementResult(
        holds,
        o.direction === CarrierVisitDirection.Inbound ? 'GateIn' : 'GateOut',
        o.direction,
        masterTypes,
      )

      if (orderResult.reaction) {
        if (!result.reaction || orderResult.isBlocking) {
          result.reaction = orderResult.reaction
        }
        result.holdNames = Array.from(new Set([...result.holdNames, ...orderResult.holdNames]))
        if (orderResult.isBlocking) {
          result.isBlocking = true
        }
      }
    })

    return result
  }, [truckVisitWithOrdersDto, holdItemStore.holdsByContainerJourneyId, masterTypes])

  const outOfSchedule = () => {
    const now = new Date()
    if (truckVisitWithOrdersDto.eta) {
      const etaDate = new Date(truckVisitWithOrdersDto.eta)
      if (now.getTime() < etaDate.getTime()) {
        return false
      }
    }
    if (truckVisitWithOrdersDto.etd) {
      const etdDate = new Date(truckVisitWithOrdersDto.etd)
      if (now.getTime() > etdDate.getTime()) {
        return true
      }
    }
    return false
  }

  const inboundAlreadyOnTerminal =
    truckVisitWithOrdersDto?.isTruckWithLicensePlateOnTerminal === true &&
    direction === CarrierVisitDirection.Inbound // Show Gate In trucks

  const handleCardClick = () => {
    if (inboundAlreadyOnTerminal || enforcement.isBlocking) {
      return
    }
    onClick(truckVisitWithOrdersDto)
  }

  return (
    <Card
      sx={{
        width: '100%',
        opacity: inboundAlreadyOnTerminal || enforcement.isBlocking ? 0.7 : 1,
        cursor: inboundAlreadyOnTerminal || enforcement.isBlocking ? 'not-allowed' : 'pointer',
      }}
      onClick={handleCardClick}
      data-cy={`truck-visit-${truckVisitWithOrdersDto.identifier}`}
    >
      <CardContent>
        <Stack>
          <Stack direction={'row'} spacing={1} alignItems='center' justifyContent='space-between'>
            <Stack direction={'row'} spacing={1}>
              <TruckIcon />
              <Typography variant='subtitle1'>
                <HighlightedText
                  highlight={searchBy === TruckAppointmentSearchBy.TruckPlate ? highlight : ''}
                  color={theme.palette.info.main}
                >
                  {truckVisitWithOrdersDto.identifier}
                </HighlightedText>
              </Typography>
            </Stack>
            {outOfSchedule() && (
              <ColoredLabel color='warning' label={t('outOfSchedule', 'Out of schedule')} />
            )}
            {inboundAlreadyOnTerminal && (
              <ColoredLabel color='warning' label={t('alreadyOnTerminal', 'Already on terminal')} />
            )}
          </Stack>
          <>
            {truckVisitWithOrdersDto.trailerPlateNumber && (
              <Stack direction={'row'} spacing={1}>
                <TrailerIcon />
                <Typography variant='subtitle1'>
                  {truckVisitWithOrdersDto.trailerPlateNumber}
                </Typography>
              </Stack>
            )}
            <TruckVisitDriver
              truckVisit={truckVisitWithOrdersDto}
              highlight={searchBy === TruckAppointmentSearchBy.DriverID ? highlight : ''}
              isSearch
            />

            {truckVisitWithOrdersDto.inboundOrders?.map((order, index) => (
              <TruckVisitSearchCargoDetails order={order} key={order.id} isFirst={index === 0} />
            ))}

            {truckVisitWithOrdersDto.outboundOrders?.map((order, index) => (
              <TruckVisitSearchCargoDetails order={order} key={order.id} isFirst={index === 0} />
            ))}

            <TruckVisitExpectedArrival truckVisit={truckVisitWithOrdersDto} />

            <HoldEnforcementAlert reaction={enforcement.reaction} holdNames={enforcement.holdNames} />
          </>
        </Stack>
      </CardContent>
    </Card>
  )
})
